import {SimpleProductType} from '@optimaxdev/analytics/mobile/types/productTypes';
import {getProductType} from '@optimaxdev/analytics/mobile/utils/getProductType/getProductType';

import {PRODUCT_TYPES} from 'constants/product';
import {
    BaseCartItemType,
    CartItemContactsType,
    CartItemGlassesTypeV2Type,
    CartItemType,
} from 'desktop/pages/cart/types';

import {isOneSignalEnabled, sendOneSignalPurchaseData} from 'features/oneSignal/oneSignal';

import {
    PAGE_NAME_FOR_VIZURY,
    PagesLocationType,
    VALUE_PAGE_FOR_VIZURY,
    VizuryDataFromTYPType,
} from './vizury.types';

/**
 * Manual parsing data to vizury for data collection in spa mode
 * @param {string} page - page name
 */
const manualParseDataForVizury = (page: keyof typeof VALUE_PAGE_FOR_VIZURY) => {
    const dataForParse = VALUE_PAGE_FOR_VIZURY[page];

    if (dataForParse && window.pixel) {
        window.pixel.parse(dataForParse);
    }
};

/**
 * Set data to vizLayer on cart page
 * @param {SimpleProductType} quoteItems - quoteItems data
 * @param {string} cartKey - cart key
 */
const vizuryForCart = (
    quoteItems: CartItemGlassesTypeV2Type[] | CartItemContactsType[],
    cartKey: string,
) => {
    const quoteItemsData: {[key: string]: string | number | null | string[]} = {};
    let grandTotalInCart = 0;
    if (quoteItems.length) {
        // eslint-disable-next-line max-statements
        quoteItems.forEach((item, index) => {
            const isContact = item.type === PRODUCT_TYPES.CONTACT_LENSES;
            grandTotalInCart += item.totalFinalPrice;
            quoteItemsData[`ptitle${index}`] = item.name;
            quoteItemsData[`price${index}`] = isContact ? item.price : item.totalFinalPrice;
            quoteItemsData[`pbrand${index}`] = item.brand;
            quoteItemsData[`pid${index}`] = item.product_sku;
            quoteItemsData[`psku${index}`] = item.sku;
            quoteItemsData[`psize${index}`] = isContact
                ? null
                : (item as CartItemGlassesTypeV2Type).frame.frameSize;
            quoteItemsData[`pcolor${index}`] = isContact
                ? null
                : (item as CartItemGlassesTypeV2Type).frame.color;
            quoteItemsData[`pgender${index}`] = isContact
                ? null
                : (item as CartItemGlassesTypeV2Type).gender?.join(', ');
            quoteItemsData[`ptype${index}`] = item.type;
            quoteItemsData[`pbadge${index}`] = (item as CartItemGlassesTypeV2Type).badges?.length
                ? (item as CartItemGlassesTypeV2Type).badges[0].title
                : null;
            quoteItemsData[`catid${index}`] = isContact
                ? ''
                : (item as CartItemGlassesTypeV2Type).glasses_type;
            quoteItemsData[`pimg${index}`] = item.img || '';
            quoteItemsData[`purl${index}`] = `${window.location.origin}/${item.frame_url_path}`;
            quoteItemsData[`quantity${index}`] = item.qty;
        });
    }
    window.vizLayer = {
        carturl: `https://www.glassesusa.com/checkout/cart?${cartKey}`,
        cartval: grandTotalInCart,
        currency: 'USD',
        type: PAGE_NAME_FOR_VIZURY.CART,
        ...quoteItemsData,
    };

    manualParseDataForVizury(PAGE_NAME_FOR_VIZURY.CART);
};

// TODO: Need to add orderedItems types to vizuryForTYP from mobile version
/**
 * Set data to vizLayer on TYP
 * @param {VizuryDataFromTYPType} dataForTYP - quoteItems data
 * @param {string} cartKey - cart key
 */
const vizuryForTYP = ({id, orderedItems, revenue}: VizuryDataFromTYPType) => {
    const additionalDataForTYP: {[key: string]: string | number} = {};

    if (orderedItems.length) {
        orderedItems.forEach((item, index) => {
            // @ts-expect-error
            additionalDataForTYP[`price${index}`] = item?.discounted_price || item.discountedPrice;
            // @ts-expect-error
            additionalDataForTYP[`pid${index}`] = item.product_sku || item.product.sku;
            // @ts-expect-error
            additionalDataForTYP[`catid${index}`] = item.type || getProductType(item.product.type);
            additionalDataForTYP[`quantity${index}`] = item.qty;
        });
    }

    const analyticsData = {
        currency: 'USD',
        isvizconv: 0,
        orderid: id,
        orderprice: revenue,
        type: PAGE_NAME_FOR_VIZURY.TYP,
        ...additionalDataForTYP,
    };

    if (isOneSignalEnabled()) {
        sendOneSignalPurchaseData(analyticsData);
    } else {
        window.vizLayer = analyticsData;
    }

    manualParseDataForVizury(PAGE_NAME_FOR_VIZURY.TYP);
};

type PushVizuryArgumentsType = {
    location: PagesLocationType;
    data?: string | SimpleProductType | BaseCartItemType[] | VizuryDataFromTYPType | CartItemType[];
    cartKey?: string;
    href?: string;
};

/**
 * Call necessary function which depends on current page
 * @param {string} location - call`s location
 * @param {string | SimpleProductType | BaseCartItemType[] | VizuryDataFromTYPType} data - data for push to vizury
 * @param {string} cartKey - cart key
 */
export const pushVizury = (args: PushVizuryArgumentsType) => {
    window.vizLayer = {};

    switch (args.location) {
        case PAGE_NAME_FOR_VIZURY.CART:
            vizuryForCart(
                args.data as CartItemGlassesTypeV2Type[] | CartItemContactsType[],
                args.cartKey || '',
            );
            break;
        case PAGE_NAME_FOR_VIZURY.TYP:
            vizuryForTYP(args.data as VizuryDataFromTYPType);
            break;
        default:
            return;
    }
};
